import React, { Fragment, useState, useRef, useCallback, useMemo, useEffect } from "react";
import ReactDOM from "react-dom";
import { useKeycloak } from "@react-keycloak/web";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FormButton from "../../components/form-components/FormButton.js";
import "@fortawesome/fontawesome-svg-core/styles.css";
import { toast } from '../../components/ui-components/Toast';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import OrgChart from "../../components/org-chart/OrgChart.jsx";
import {
  getCustomerMe,
  getBudgetDimensions,
  getCostModelVersions,
  createCostModelVersion,
  applyCostModelVersion,
  archiveCostModelVersion,
  getCostModelNodes,
  getCostModelEdges,
  createCostModelNode,
  updateCostModelNode,
  deleteCostModelNode,
  putCostModelEdges,
  getCostModelNodeCosts,
  getPeriodLock,
} from "./api/costModelApi.js";
import useHierarchyStore from "../../store/hierarchyStore.js";
import Tooltip from "../../components/form-components/Tooltip.jsx";
import { pctOfParent } from "../../components/org-chart/costFormat.js";
import { hiddenDescendants } from "../../components/org-chart/collapse.js";
import { ancestorChain } from "../../components/org-chart/ancestors.js";
import { computeDraftDiff } from "../../components/org-chart/draftDiff.js";
import NodeHistoryDrawer from "./NodeHistoryDrawer.jsx";
import RightSheet from "../../components/ui-components/RightSheet.jsx";
import { formatValue } from "../../components/Utils.js";

// The catch-all "Unallocated / Shared Services" node is system-managed: it can't be edited,
// moved or removed, and it must never appear as a user change in the draft diff.
const SYSTEM_NODE_CODE = "__unallocated__";
const isSystemNode = (code) => code === SYSTEM_NODE_CODE;

/** Format applied_at for status subtitle: "Updated today", "Updated 1 Jan 2025", or "Updated just now" when justApplied. */
function formatAppliedRecency(appliedAt, justApplied) {
  if (justApplied) return "Updated just now";
  if (!appliedAt) return null;
  const d = appliedAt instanceof Date ? appliedAt : new Date(appliedAt);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const sameDay = d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  if (sameDay) return "Updated today";
  return `Updated ${d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
}

/** Format a period-lock date ("2026-04-30") as "30 Apr 2026"; returns the raw string if unparseable. */
function formatLockDate(s) {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** Format the node-costs window (the API's `period` field) as a concrete range like
 *  "1 Jul 2025 – 30 Jun 2026" for the org-chart caption. Defensive about the shape: accepts a
 *  range string ("2025-07-01..2026-06-30") or an object ({from,to}/{start,end}/{start_date,end_date}).
 *  Returns null when two dates can't be parsed, so callers fall back to "last 12 months". */
function formatCostPeriod(period) {
  if (!period) return null;
  let from = null;
  let to = null;
  if (typeof period === "string") {
    const m = period.match(/(\d{4}-\d{2}-\d{2})\D+(\d{4}-\d{2}-\d{2})/);
    if (!m) return null;
    [, from, to] = m;
  } else if (typeof period === "object") {
    from = period.from ?? period.start ?? period.start_date ?? period.startDate ?? null;
    to = period.to ?? period.end ?? period.end_date ?? period.endDate ?? null;
  }
  const fmt = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  };
  const f = fmt(from);
  const t = fmt(to);
  return f && t ? `${f} – ${t}` : null;
}

/** Slugify name to a valid code: lowercase, trim, whitespace and - to _, non [a-z0-9_] removed, collapse _, trim; empty => "node". */
function slugifyCode(name) {
  if (name == null) return "node";
  let s = String(name).trim().toLowerCase().replace(/-/g, "_");
  s = s.replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
  while (s.includes("__")) s = s.replace(/__/g, "_");
  s = s.replace(/^_|_$/g, "");
  return s || "node";
}

// Perspective (dimension) tab icons, keyed by a normalised code/name. The icon() macro needs
// string-literal args, so each is precomputed and selected at runtime; unknown dimensions fall
// back to the generic layer-group.
const DIMENSION_ICONS = {
  org: icon({ name: "sitemap", family: "sharp", style: "light" }),
  organisation: icon({ name: "sitemap", family: "sharp", style: "light" }),
  organization: icon({ name: "sitemap", family: "sharp", style: "light" }),
  financial: icon({ name: "coins", family: "sharp", style: "light" }),
  finance: icon({ name: "coins", family: "sharp", style: "light" }),
  cost: icon({ name: "coins", family: "sharp", style: "light" }),
  costs: icon({ name: "coins", family: "sharp", style: "light" }),
  technical: icon({ name: "microchip", family: "sharp", style: "light" }),
  technology: icon({ name: "microchip", family: "sharp", style: "light" }),
  tech: icon({ name: "microchip", family: "sharp", style: "light" }),
  usecase: icon({ name: "bullseye", family: "sharp", style: "light" }),
  usecases: icon({ name: "bullseye", family: "sharp", style: "light" }),
};
const DEFAULT_DIMENSION_ICON = icon({ name: "layer-group", family: "sharp", style: "light" });
const getDimensionIcon = (d) => {
  const norm = (s) => String(s ?? "").toLowerCase().replace(/[\s_-]/g, "");
  // Match the dimension's code first, then fall back to its name (codes are env-specific and may
  // not map, but the display name almost always does).
  return DIMENSION_ICONS[norm(d.code)] || DIMENSION_ICONS[norm(d.name)] || DEFAULT_DIMENSION_ICON;
};

const Dialog = ({ show, close, title, subtitle, children, footer, width = "w-[500px]", onEnter }) => {
  // Escape cancels, Enter triggers the primary action (when onEnter is provided). Enter is ignored
  // inside a textarea / contenteditable so multi-line input still works.
  useEffect(() => {
    if (!show) return;
    const handleKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (e.key === "Escape") {
        e.preventDefault();
        close?.();
      } else if (e.key === "Enter" && onEnter && tag !== "textarea" && !e.target?.isContentEditable) {
        e.preventDefault();
        onEnter();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [show, close, onEnter]);
  if (!show) return null;
  return ReactDOM.createPortal(
    <div className="bg-black bg-opacity-50 z-[9999] fixed grid content-center place-content-center items-center top-0 left-0 right-0 w-full p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] max-h-full cursor-default text-text">
      <div className={`${width} bg-card relative border-card-bd border rounded-card flex flex-col z-[10000]`} onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-row justify-between items-start p-4 border-card-bd">
          <div className="flex flex-col ml-4 mt-2">
            <div className="text-text-3 uppercase text-sm">{subtitle}</div>
            <div className="text-text text-xl font-semibold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {title}
            </div>
          </div>
          <button onClick={close} className="cursor-pointer" aria-label="Close dialog">
            <FontAwesomeIcon icon={icon({ name: "xmark", family: "sharp", style: "solid" })} className="w-6 h-6 text-text-3 text-[20px] mr-2 mt-1" />
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
        <div className="flex justify-start gap-2 py-5 pl-4 pr-4">
          {footer}
        </div>
      </div>
    </div>,
    document.body
  );
};

/** Full-width action button for the node inspector. Reuses the app's inline action-button colour
 *  tokens (see InlineButton) but sized to stack in the narrow inspector panel. `kbd` surfaces the
 *  keyboard shortcut that triggers the same action. */
const InspectorAction = ({ onClick, iconDef, label, variant = "secondary", kbd }) => {
  const styles = {
    primary: "text-white bg-primary hover:bg-primary-hover border border-transparent",
    secondary: "text-text-2 bg-card border border-card-bd hover:bg-surface-2 hover:text-text",
    danger: "text-danger bg-card border border-card-bd hover:bg-danger-soft hover:border-danger",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full inline-flex items-center gap-2.5 rounded-ctrl px-3 py-2 text-sm font-medium transition-colors ${styles[variant]}`}
    >
      <FontAwesomeIcon icon={iconDef} className="w-4 text-center" />
      <span className="flex-1 text-left">{label}</span>
      {kbd && (
        <kbd className="text-[10px] font-normal text-text-2 bg-surface-2 border border-card-bd rounded px-1.5 py-0.5 leading-none">
          {kbd}
        </kbd>
      )}
    </button>
  );
};

const VIEW_MODE_STORAGE_PREFIX = "hierarchy_view_mode:";

function getStoredViewMode(customerId, dimensionCode) {
  try {
    const key = `${VIEW_MODE_STORAGE_PREFIX}${customerId}:${dimensionCode}`;
    const raw = localStorage.getItem(key);
    if (raw === "active" || raw === "draft") return raw;
  } catch (_) {}
  return null;
}

function setStoredViewMode(customerId, dimensionCode, viewMode) {
  try {
    const key = `${VIEW_MODE_STORAGE_PREFIX}${customerId}:${dimensionCode}`;
    localStorage.setItem(key, viewMode);
  } catch (_) {}
}

const HierarchyManager = () => {
  const { keycloak } = useKeycloak();
  const queryClient = useQueryClient();
  const customerQuery = useQuery({
    queryKey: ["customer-me"],
    queryFn: getCustomerMe,
    enabled: !!keycloak?.authenticated,
  });
  const customer = customerQuery.data ?? null;
  const customerId = customer?.id ?? customer?.ID ?? "default";
  const customerLabel =
    customer?.name ??
    (keycloak?.realm ? String(keycloak.realm).replace(/\b\w/g, (c) => c.toUpperCase()) : "Root");
  const { selectedDimensionId, setSelectedDimensionId, collapsedByKey, setCollapsedForKey } = useHierarchyStore();
  /** Single source of truth: "active" | "draft". Tree and actions are driven by this. */
  const [viewMode, setViewMode] = useState("active");
  /** Show pro-forma (simulated, not-yet-booked) node costs instead of actuals. Active view only. */
  const [proforma, setProforma] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [applyNote, setApplyNote] = useState("");
  // Effective date for applying a draft (when the new structure takes effect). Defaults today.
  const [applyEffectiveDate, setApplyEffectiveDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [addNodeModalOpen, setAddNodeModalOpen] = useState(false);
  const [addNodeParentId, setAddNodeParentId] = useState(null);
  const [addNodeName, setAddNodeName] = useState("");
  const [addNodeCode, setAddNodeCode] = useState("");
  const [addNodeCodeTouched, setAddNodeCodeTouched] = useState(false);
  const [addNodeAdvancedOpen, setAddNodeAdvancedOpen] = useState(false);
  const [addNodeCodeError, setAddNodeCodeError] = useState(null);
  const [addingToParentId, setAddingToParentId] = useState(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameNode, setRenameNode] = useState(null);
  const [renameName, setRenameName] = useState("");
  const [renameCode, setRenameCode] = useState("");
  const [renameCodeTouched, setRenameCodeTouched] = useState(false);
  const [editAdvancedOpen, setEditAdvancedOpen] = useState(false);
  const [editCodeError, setEditCodeError] = useState(null);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveNodeId, setMoveNodeId] = useState(null);
  const [moveTargetParentId, setMoveTargetParentId] = useState(null);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removeNodeId, setRemoveNodeId] = useState(null);
  const [removeNodeName, setRemoveNodeName] = useState("");
  const [removingNode, setRemovingNode] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const orgChartContainerRef = useRef(null);
  /** Set when draft was applied this session; used for "Updated just now" and cleared after a delay. */
  const [justAppliedVersionId, setJustAppliedVersionId] = useState(null);
  useEffect(() => {
    if (justAppliedVersionId == null) return;
    const t = setTimeout(() => setJustAppliedVersionId(null), 8000);
    return () => clearTimeout(t);
  }, [justAppliedVersionId]);

  const dimensionsQuery = useQuery({
    queryKey: ["budget-dimensions"],
    queryFn: getBudgetDimensions,
  });
  const dimensionsRaw = dimensionsQuery.data ?? [];
  // Organisation leads — it's the primary ownership lens — then the rest keep their order.
  const isOrgDimension = (d) => (d.code === "org") || String(d.name ?? "").toLowerCase().startsWith("organi");
  const dimensions = dimensionsRaw
    .map((d) => ({
      id: d.id ?? d.ID,
      code: d.code ?? d.Code,
      name: d.name ?? d.Name,
    }))
    .sort((a, b) => (isOrgDimension(a) ? 0 : 1) - (isOrgDimension(b) ? 0 : 1));

  useEffect(() => {
    if (dimensions.length === 0) return;
    const storedIdValid = dimensions.some((d) => d.id === selectedDimensionId);
    if (!storedIdValid) {
      setSelectedDimensionId(dimensions[0].id);
    }
  }, [dimensions, selectedDimensionId, setSelectedDimensionId]);

  const versionsQuery = useQuery({
    queryKey: ["cost-model-versions", selectedDimensionId],
    queryFn: () => getCostModelVersions(selectedDimensionId),
    enabled: selectedDimensionId != null,
  });
  const versionsRaw = versionsQuery.data ?? [];
  const versions = versionsRaw.map((v) => ({
    id: v.id ?? v.ID,
    status: v.status ?? v.Status,
    applied_at: v.applied_at ?? v.AppliedAt ?? null,
    applied_by_user_id: v.applied_by_user_id ?? v.AppliedByUserID ?? null,
    applied_by_name: v.applied_by_name ?? v.AppliedByName ?? null,
    created_at: v.created_at ?? v.CreatedAt ?? null,
    created_by_name: v.created_by_name ?? v.CreatedByName ?? null,
  }));
  const activeVersion = versions.find((v) => (v.status || "").toLowerCase() === "active") || null;
  const draftVersion = versions.find((v) => (v.status || "").toLowerCase() === "draft") || null;
  const activeVersionId = activeVersion?.id ?? null;
  const draftVersionId = draftVersion?.id ?? null;
  const hasDraft = !!draftVersion;
  const selectedDimension = dimensions.find((d) => d.id === selectedDimensionId) ?? null;
  const dimensionCode = selectedDimension?.code ?? String(selectedDimensionId ?? "");

  /** Selected version is derived from viewMode; all tree and CRUD use this. */
  const selectedVersionId = viewMode === "draft" ? draftVersionId : activeVersionId;
  const canEdit = viewMode === "draft";

  // Initialize or restore viewMode only when dimension (or customer) changes
  const dimensionKeyRef = useRef(null);
  useEffect(() => {
    if (selectedDimensionId == null || dimensions.length === 0) return;
    const dimensionKey = `${customerId}:${dimensionCode}`;
    if (dimensionKeyRef.current === dimensionKey) return;
    dimensionKeyRef.current = dimensionKey;
    const stored = getStoredViewMode(customerId, dimensionCode);
    const validStored = stored === "draft" ? hasDraft : true;
    if (validStored && stored) {
      setViewMode(stored);
      return;
    }
    // Default to Active when an applied version exists (it carries real costs); only open a
    // dimension in Draft when there is no active version to show yet.
    setViewMode(activeVersionId != null ? "active" : (hasDraft ? "draft" : "active"));
  }, [selectedDimensionId, dimensionCode, customerId, hasDraft, activeVersionId, dimensions.length]);

  // Guardrail: viewing draft but no draft (e.g. applied) -> switch to active
  useEffect(() => {
    if (viewMode === "draft" && draftVersionId == null) setViewMode("active");
  }, [viewMode, draftVersionId]);

  // Persist viewMode per dimension when it changes
  useEffect(() => {
    if (!dimensionCode) return;
    setStoredViewMode(customerId, dimensionCode, viewMode);
  }, [viewMode, customerId, dimensionCode]);

  const periodLockQuery = useQuery({ queryKey: ["period-lock"], queryFn: getPeriodLock });
  const lockedThrough = periodLockQuery.data || null;

  const nodesQuery = useQuery({
    queryKey: ["cost-model-nodes", selectedVersionId],
    queryFn: () => getCostModelNodes(selectedVersionId),
    enabled: selectedVersionId != null,
  });
  const edgesQuery = useQuery({
    queryKey: ["cost-model-edges", selectedVersionId],
    queryFn: () => getCostModelEdges(selectedVersionId),
    enabled: selectedVersionId != null,
  });
  const nodesRaw = (nodesQuery.data ?? []).map((n) => ({
    node_id: n.node_id ?? n.NodeID,
    name: n.name ?? n.Name,
    code: n.code ?? n.Code,
    code_editable: n.code_editable ?? n.CodeEditable ?? true,
  }));
  const edgesRaw = (edgesQuery.data ?? []).map((e) => ({
    parent_node_id: e.parent_node_id ?? e.ParentNodeID,
    child_node_id: e.child_node_id ?? e.ChildNodeID,
  }));

  // Allocated cost per node (direct + subtree rollup, trailing 12 months) from the ACTIVE
  // version. Fetched in both views: in Active it's the live figure; in Draft we show it as a
  // muted "active" reference, mapped onto draft nodes by their stable code (draft nodes have
  // different ids but keep the same code). Keyed on activeVersionId so the cache is shared.
  const costsActive = viewMode === "active";
  const nodeCostsQuery = useQuery({
    queryKey: ["node-costs", activeVersionId, proforma],
    queryFn: () => getCostModelNodeCosts(activeVersionId, { proforma }),
    enabled: activeVersionId != null,
    keepPreviousData: true,
  });
  const costByCode = {};
  for (const c of nodeCostsQuery.data?.nodes ?? []) {
    if (c.code) costByCode[c.code] = { direct: c.direct ?? 0, subtree: c.subtree ?? 0 };
  }
  // Displayed node id → stable code (for resolving a node's parent cost when computing share).
  const codeByNodeId = {};
  nodesRaw.forEach((n) => { codeByNodeId[n.node_id] = n.code; });
  const anyCosts = Object.keys(costByCode).length > 0;
  // The window the figures cover. The period selector was removed (figures are fixed to the
  // trailing 12 months), so the org chart states the period itself: a concrete range from the
  // API when available, otherwise the "last 12 months" wording.
  const costPeriodLabel = formatCostPeriod(nodeCostsQuery.data?.period) || "last 12 months";

  // Draft↔Active diff: in Draft view, fetch the active version's nodes/edges and classify each
  // draft node (added/moved/renamed) against it by stable code, plus a removed count.
  const activeNodesQuery = useQuery({
    queryKey: ["cost-model-nodes", activeVersionId],
    queryFn: () => getCostModelNodes(activeVersionId),
    enabled: viewMode === "draft" && activeVersionId != null,
  });
  const activeEdgesQuery = useQuery({
    queryKey: ["cost-model-edges", activeVersionId],
    queryFn: () => getCostModelEdges(activeVersionId),
    enabled: viewMode === "draft" && activeVersionId != null,
  });
  const toCodeList = (nodes, edges) => {
    const idToCode = {};
    (nodes ?? []).forEach((n) => { idToCode[n.node_id ?? n.NodeID] = n.code ?? n.Code; });
    const parentById = {};
    (edges ?? []).forEach((e) => { parentById[e.child_node_id ?? e.ChildNodeID] = e.parent_node_id ?? e.ParentNodeID; });
    return (nodes ?? []).map((n) => {
      const id = n.node_id ?? n.NodeID;
      const pid = parentById[id];
      return { code: n.code ?? n.Code, name: n.name ?? n.Name, parentCode: pid != null ? (idToCode[pid] ?? null) : null };
    });
  };
  // System nodes (the catch-all) are excluded from the diff so it's never reported as a user
  // add/move/remove — its presence is managed by the platform, not the editor.
  const stripSystem = (list) => list.filter((n) => !isSystemNode(n.code));
  const draftDiff = (viewMode === "draft" && activeNodesQuery.data && activeEdgesQuery.data)
    ? computeDraftDiff(
        stripSystem(toCodeList(activeNodesQuery.data, activeEdgesQuery.data)),
        stripSystem(toCodeList(nodesRaw, edgesRaw))
      )
    : null;
  // Removed nodes aren't in the draft tree, so name them explicitly (governance: you must see
  // exactly what an Apply will remove). Names resolved from the active version.
  const removedNames = (draftDiff?.removed || []).map((code) => {
    const n = (activeNodesQuery.data || []).find((x) => (x.code ?? x.Code) === code);
    return n ? (n.name ?? n.Name) : code;
  });
  // Detailed change list for the review panel — each row with before→after + the draft node id
  // for click-to-center.
  const reviewChanges = (() => {
    if (!draftDiff) return [];
    const active = stripSystem(toCodeList(activeNodesQuery.data, activeEdgesQuery.data));
    const draft = stripSystem(toCodeList(nodesRaw, edgesRaw));
    const activeByCode = new Map(active.map((n) => [n.code, n]));
    const draftByCode = new Map(draft.map((n) => [n.code, n]));
    const draftIdByCode = {};
    nodesRaw.forEach((n) => { draftIdByCode[n.code] = String(n.node_id); });
    const parentName = (code) => (code ? (draftByCode.get(code)?.name ?? activeByCode.get(code)?.name ?? code) : "top level");
    const rows = [];
    for (const [code, kind] of Object.entries(draftDiff.byCode)) {
      if (kind === "unchanged") continue;
      const d = draftByCode.get(code);
      const a = activeByCode.get(code);
      let detail = "New";
      if (kind === "renamed") detail = `"${a?.name}" → "${d?.name}"`;
      else if (kind === "moved") detail = `${parentName(a?.parentCode)} → ${parentName(d?.parentCode)}`;
      rows.push({ kind, code, name: d?.name ?? code, nodeId: draftIdByCode[code] ?? null, detail });
    }
    for (const code of draftDiff.removed) {
      rows.push({ kind: "removed", code, name: activeByCode.get(code)?.name ?? code, nodeId: null, detail: "Removed from the hierarchy" });
    }
    const order = { added: 0, moved: 1, renamed: 2, removed: 3 };
    return rows.sort((x, y) => order[x.kind] - order[y.kind]);
  })();
  const reviewJumpTo = (nodeId) => {
    if (!nodeId) return;
    setSelectedNodeId(nodeId);
    focusNonceRef.current += 1;
    setFocusReq({ id: nodeId, n: focusNonceRef.current });
    setReviewOpen(false);
  };

  // Human-readable change summary, recorded in the apply audit trail.
  const changeSummaryText = draftDiff
    ? [
        draftDiff.counts.added ? `+${draftDiff.counts.added} added` : null,
        draftDiff.counts.moved ? `${draftDiff.counts.moved} moved` : null,
        draftDiff.counts.renamed ? `${draftDiff.counts.renamed} renamed` : null,
        draftDiff.counts.removed ? `${draftDiff.counts.removed} removed` : null,
      ].filter(Boolean).join(", ")
    : "";

  useEffect(() => {
    if (!addNodeModalOpen) return;
    if (!addNodeCodeTouched) setAddNodeCode(slugifyCode(addNodeName));
  }, [addNodeModalOpen, addNodeName, addNodeCodeTouched]);

  const refetchTree = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["cost-model-nodes", selectedVersionId] });
    queryClient.invalidateQueries({ queryKey: ["cost-model-edges", selectedVersionId] });
  }, [queryClient, selectedVersionId]);


  const awaitRefetchVersions = useCallback(() => {
    return queryClient.refetchQueries({ queryKey: ["cost-model-versions", selectedDimensionId] });
  }, [queryClient, selectedDimensionId]);

  const handleCreateDraft = useCallback(() => {
    if (!selectedDimensionId) return;
    setCreatingDraft(true);
    const basedOn = activeVersion ? activeVersion.id : null;
    createCostModelVersion({
      dimension_id: selectedDimensionId,
      based_on_version_id: basedOn ?? undefined,
    })
      .then((v) => {
        const newVersionId = v?.id ?? v?.ID;
        return queryClient.refetchQueries({ queryKey: ["cost-model-versions", selectedDimensionId] }).then(() => {
          setViewMode("draft");
          if (newVersionId) {
            queryClient.invalidateQueries({ queryKey: ["cost-model-nodes", newVersionId] });
            queryClient.invalidateQueries({ queryKey: ["cost-model-edges", newVersionId] });
          }
          toast.success("Draft created. You can now edit the hierarchy.");
        });
      })
      .catch((err) => {
        toast.error(err?.message || "Could not create draft. Please try again.");
      })
      .finally(() => setCreatingDraft(false));
  }, [selectedDimensionId, activeVersion, queryClient]);

  const parentByChild = {};
  edgesRaw.forEach((e) => {
    parentByChild[e.child_node_id] = e.parent_node_id;
  });
  // String child-id → parent-id map ("root" for top-level), for revealing a node on search.
  const parentIdByChild = useMemo(() => {
    const map = {};
    edgesRaw.forEach((e) => {
      map[String(e.child_node_id)] = e.parent_node_id != null ? String(e.parent_node_id) : "root";
    });
    return map;
  }, [edgesRaw]);
  // Direct-child count per node, for the collapse affordance + collapsed badge.
  const childCountByParent = {};
  edgesRaw.forEach((e) => {
    if (e.parent_node_id != null) {
      childCountByParent[e.parent_node_id] = (childCountByParent[e.parent_node_id] || 0) + 1;
    }
  });
  // Ordered children per parent (parent key "root" for top-level), for arrow-key navigation.
  const childrenByParent = {};
  nodesRaw.forEach((n) => {
    const p = parentByChild[n.node_id];
    const key = p != null ? String(p) : "root";
    (childrenByParent[key] = childrenByParent[key] || []).push(String(n.node_id));
  });
  // Live children map read by the toggle handler at click time (so the handler isn't recreated each
  // render). Used to reveal a single level on expand — newly shown children stay collapsed.
  const childrenByParentRef = useRef({});
  childrenByParentRef.current = childrenByParent;

  // Collapse/expand: collapsedIds holds reactFlow node ids (strings) whose subtrees are folded
  // away. In-memory (resets on reload) — a lightweight view aid, not persisted state.
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  // The most recently folded/unfolded node — the chart keeps it stationary on screen across
  // the re-layout so folding doesn't make the clicked node jump.
  const [lastToggledId, setLastToggledId] = useState(null);
  // Persistence key for the fold state — per perspective + view. A ref so the toggle handlers read
  // the current key without being recreated on every dimension/view change.
  // "v2" namespace: the fold semantics changed (expand reveals one level at a time), so older saved
  // states are ignored once and everyone gets the correct default.
  const collapseKey = `v2:${selectedDimensionId}:${viewMode}`;
  const collapseKeyRef = useRef(collapseKey);
  collapseKeyRef.current = collapseKey;
  const handleToggleCollapse = useCallback((id) => {
    setLastToggledId(id);
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        // Expand ONE level only: reveal this node's direct children, but keep any child that itself
        // has children collapsed, so deeper levels stay hidden until they're expanded in turn.
        next.delete(id);
        (childrenByParentRef.current[id] || []).forEach((childId) => {
          if ((childrenByParentRef.current[childId] || []).length > 0) next.add(childId);
        });
      } else {
        next.add(id);
      }
      setCollapsedForKey(collapseKeyRef.current, [...next]); // persist to the zustand store
      return next;
    });
  }, [setCollapsedForKey]);

  // On each perspective/view load, restore the user's saved fold state from the store; if there's
  // none yet, start folded (only the root + its direct children show, everything deeper collapsed)
  // and persist that as the default. Manual fold/unfold afterwards is saved by the toggle handler.
  const defaultFoldKeyRef = useRef(null);
  useEffect(() => {
    if (!nodesQuery.isSuccess || !edgesQuery.isSuccess || nodesRaw.length === 0) return;
    const initKey = `${selectedDimensionId}:${viewMode}:${selectedVersionId}`;
    if (defaultFoldKeyRef.current === initKey) return;
    defaultFoldKeyRef.current = initKey;
    const storeKey = `v2:${selectedDimensionId}:${viewMode}`;
    const stored = collapsedByKey[storeKey];
    setLastToggledId(null);
    if (Array.isArray(stored)) {
      setCollapsedIds(new Set(stored));
      return;
    }
    const parentOf = {};
    const hasChild = new Set();
    edgesRaw.forEach((e) => {
      parentOf[e.child_node_id] = e.parent_node_id;
      if (e.parent_node_id != null) hasChild.add(e.parent_node_id);
    });
    const topLevelWithChildren = nodesRaw
      .filter((n) => parentOf[n.node_id] == null && hasChild.has(n.node_id))
      .map((n) => String(n.node_id));
    setCollapsedIds(new Set(topLevelWithChildren));
    setCollapsedForKey(storeKey, topLevelWithChildren);
  }, [nodesQuery.isSuccess, edgesQuery.isSuccess, selectedDimensionId, viewMode, selectedVersionId, nodesRaw, edgesRaw, collapsedByKey, setCollapsedForKey]);

  // Jump-to-node (search): reveal the node by un-collapsing any ancestor that hides it, then
  // ask the chart to center on it. focusReq carries a nonce so repeat selections re-fire.
  const [searchQuery, setSearchQuery] = useState("");
  const [focusReq, setFocusReq] = useState(null);
  const focusNonceRef = useRef(0);
  const handleJumpToNode = useCallback((nodeId) => {
    const sid = String(nodeId);
    const ancestors = ancestorChain(sid, parentIdByChild);
    if (ancestors.length) {
      setCollapsedIds((prev) => {
        const next = new Set(prev);
        ancestors.forEach((a) => next.delete(a));
        setCollapsedForKey(collapseKeyRef.current, [...next]); // persist the reveal
        return next;
      });
    }
    focusNonceRef.current += 1;
    setFocusReq({ id: sid, n: focusNonceRef.current });
    setSearchQuery("");
  }, [parentIdByChild, setCollapsedForKey]);
  const searchMatches = searchQuery.trim().length >= 1
    ? nodesRaw
        .filter((n) => String(n.name ?? "").toLowerCase().includes(searchQuery.trim().toLowerCase()))
        .slice(0, 8)
    : [];

  // Per-node change history drawer (read-only; available in both Active and Draft).
  const [historyNode, setHistoryNode] = useState(null);
  // Draft "review changes" panel.
  const [reviewOpen, setReviewOpen] = useState(false);

  // Node selection drives the contextual action bar (first-class, keyboard-accessible actions).
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  // Live tree maps for keyboard navigation, read by the global key handler at event time.
  // (childrenByParent/parentByChild are computed above; assigned here after navRef exists.)
  const navRef = useRef({});
  navRef.current = { selectedNodeId, parentByChild, childrenByParent };

  // Focus mode: double-click a node to view only its subtree (it becomes a temporary root).
  const [focusedNodeId, setFocusedNodeId] = useState(null);
  const handleFocusNode = useCallback((nodeId) => {
    if (!nodeId || nodeId === "root") return;
    const sid = String(nodeId);
    setFocusedNodeId(sid);
    focusNonceRef.current += 1;
    setFocusReq({ id: sid, n: focusNonceRef.current, mode: "fit" }); // frame the focused subtree
  }, []);
  const handleClearFocus = useCallback(() => {
    setFocusedNodeId(null);
    // Reframe the whole tree (fitView) rather than leaving the viewport parked on the node that
    // was focused.
    focusNonceRef.current += 1;
    setFocusReq({ id: "root", n: focusNonceRef.current, mode: "fit" });
  }, []);
  // Leaving the dimension or switching Active/Draft drops focus + selection (the node may not
  // exist there).
  useEffect(() => { setFocusedNodeId(null); setSelectedNodeId(null); }, [selectedDimensionId, viewMode]);
  // Keyboard: Esc clears selection; arrows move it through the tree (↑ parent, ↓ first child,
  // ←/→ siblings), centering on the new node. With a node selected, single keys trigger its
  // inspector actions: A add child, R rename, M move, H history, Del remove. With nothing
  // selected, I opens the inspector on the root. All ignored while typing in a field.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;
      if (e.key === "Escape") { setSelectedNodeId(null); return; }
      const nav = navRef.current;
      const a = nav.actions || {};
      if (!nav.selectedNodeId) {
        if (e.key === "i" || e.key === "I") { e.preventDefault(); a.openInspector?.(); }
        return;
      }
      // Action shortcuts (gated by the same rules as the inspector buttons).
      const k = e.key.toLowerCase();
      if (k === "a" && a.canEdit && !a.selSystem) { e.preventDefault(); a.addChild?.(); return; }
      if (k === "r" && a.canEdit && !a.selSystem && !a.selIsRoot) { e.preventDefault(); a.rename?.(); return; }
      if (k === "m" && a.canEdit && !a.selSystem && !a.selIsRoot) { e.preventDefault(); a.move?.(); return; }
      if (k === "h" && !a.selSystem && !a.selIsRoot) { e.preventDefault(); a.history?.(); return; }
      if (e.key === "Delete" && a.canEdit && !a.selSystem && !a.selIsRoot) { e.preventDefault(); a.remove?.(); return; }
      const id = nav.selectedNodeId;
      let next = null;
      if (e.key === "ArrowUp") {
        if (id === "root") return;
        const p = nav.parentByChild[Number(id)];
        next = p != null ? String(p) : "root";
      } else if (e.key === "ArrowDown") {
        next = (nav.childrenByParent[id] || [])[0] || null;
      } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        if (id === "root") return;
        const p = nav.parentByChild[Number(id)];
        const sibs = nav.childrenByParent[p != null ? String(p) : "root"] || [];
        const idx = sibs.indexOf(id);
        if (idx === -1) return;
        next = sibs[e.key === "ArrowLeft" ? idx - 1 : idx + 1] || null;
      } else {
        return;
      }
      if (next) {
        e.preventDefault();
        setSelectedNodeId(next);
        focusNonceRef.current += 1;
        setFocusReq({ id: next, n: focusNonceRef.current });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const onAddChild = (nodeId) => {
    setAddNodeParentId(nodeId === "root" ? null : Number.parseInt(nodeId, 10));
    setAddNodeName("");
    setAddNodeCode("");
    setAddNodeCodeTouched(false);
    setAddNodeAdvancedOpen(false);
    setAddNodeCodeError(null);
    setAddNodeModalOpen(true);
  };
  const onRemoveNode = (nodeId, nodeName) => {
    setRemoveNodeId(nodeId);
    setRemoveNodeName(nodeName ?? "this node");
    setRemoveModalOpen(true);
  };
  const handleRemoveNode = useCallback(() => {
    if (!selectedVersionId || removeNodeId == null) return;
    const nodeId = typeof removeNodeId === "string" ? Number.parseInt(removeNodeId, 10) : removeNodeId;
    if (Number.isNaN(nodeId)) return;
    setRemovingNode(true);
    deleteCostModelNode(selectedVersionId, nodeId)
      .then(() => {
        const removedName = removeNodeName || "Item";
        setRemoveModalOpen(false);
        setRemoveNodeId(null);
        setRemoveNodeName("");
        refetchTree();
        toast.success(`"${removedName}" removed from the hierarchy.`);
      })
      .catch((err) => {
        toast.error(err?.message || "Could not remove item. Please try again.");
      })
      .finally(() => setRemovingNode(false));
  }, [selectedVersionId, removeNodeId, removeNodeName, refetchTree]);

  // Selected node + contextual action-bar handlers (reuse the existing modals/handlers).
  const selIsRoot = selectedNodeId === "root";
  const selNode = !selIsRoot && selectedNodeId ? nodesRaw.find((n) => String(n.node_id) === selectedNodeId) : null;
  const selSystem = selNode ? isSystemNode(selNode.code) : false;
  const selName = selIsRoot ? customerLabel : (selNode?.name ?? "");
  const showActionBar = selIsRoot || !!selNode;
  const openRenameSelected = () => {
    if (!selNode) return;
    setRenameNode(selNode);
    setRenameName(selNode.name);
    setRenameCode(selNode.code ?? "");
    setRenameCodeTouched(false);
    setEditAdvancedOpen(false);
    setEditCodeError(null);
    setRenameModalOpen(true);
  };
  const openMoveSelected = () => {
    if (!selNode) return;
    setMoveNodeId(selectedNodeId);
    setMoveTargetParentId(parentByChild[selNode.node_id] ?? null);
    setMoveModalOpen(true);
  };

  // Expose the current selection + its action handlers to the global key handler, which is set up
  // once (empty deps) and reads them fresh at event time. Augments the nav maps assigned above.
  navRef.current.actions = {
    canEdit, selSystem, selIsRoot,
    addChild: () => onAddChild(selIsRoot ? "root" : selectedNodeId),
    rename: openRenameSelected,
    move: openMoveSelected,
    history: () => setHistoryNode({ id: selectedNodeId, name: selName }),
    remove: () => onRemoveNode(selectedNodeId, selName),
    openInspector: () => setSelectedNodeId("root"),
  };

  // Duplicate sibling-name warnings (non-blocking) for the add/rename dialogs. Duplicate names
  // under the same parent cause ambiguous cost reporting, so we flag them.
  const siblingNamesOf = (parentNodeId, excludeNodeId) =>
    new Set(
      edgesRaw
        .filter((e) => (e.parent_node_id ?? null) === (parentNodeId ?? null) && e.child_node_id !== excludeNodeId)
        .map((e) => nodesRaw.find((n) => n.node_id === e.child_node_id)?.name)
        .filter(Boolean)
    );
  const addDuplicateWarning =
    addNodeName.trim() && siblingNamesOf(addNodeParentId, null).has(addNodeName.trim())
      ? `A sibling named "${addNodeName.trim()}" already exists here.`
      : null;
  const renameParentId = renameNode ? (parentByChild[renameNode.node_id] ?? null) : null;
  const renameDuplicateWarning =
    renameNode && renameName.trim() && siblingNamesOf(renameParentId, renameNode.node_id).has(renameName.trim())
      ? `A sibling named "${renameName.trim()}" already exists.`
      : null;

  // Whole-organisation total for the root card = sum of the top-level nodes' subtree costs
  // (top-level = no parent). That includes the catch-all, so it equals the grand allocated total.
  const rootSubtree = nodesRaw.reduce((sum, n) => {
    if (parentByChild[n.node_id] != null) return sum;
    const c = costByCode[n.code];
    return c ? sum + (c.subtree ?? 0) : sum;
  }, 0);
  const rootHasData = rootSubtree !== 0;

  // ── Branch colour-coding ─────────────────────────────────────────────────────────────────
  // Each top-level branch (a direct child of the root) gets a stable colour, carried through its
  // whole subtree as a thin accent along the top of every card, so sibling subtrees read as groups
  // at a glance. The palette is deliberately muted — calm and professional for a finance audience,
  // not a vivid category wheel. The system catch-all gets its own refined slate-blue.
  const BRANCH_PALETTE = ["#6d98c6", "#75b19c", "#cda86d", "#af85b3", "#84a3c4", "#c88684", "#93b379", "#878fbd"];
  const SYSTEM_ACCENT = "#9aa5c4";
  const topAncestorId = (nodeId) => {
    let cur = String(nodeId);
    let guard = 0;
    while (parentIdByChild[cur] && parentIdByChild[cur] !== "root" && guard++ < 5000) cur = parentIdByChild[cur];
    return cur;
  };
  const branchColorByRootId = {};
  let _branchIdx = 0;
  nodesRaw
    .filter((n) => parentByChild[n.node_id] == null)
    .forEach((n) => {
      branchColorByRootId[String(n.node_id)] = isSystemNode(n.code) ? SYSTEM_ACCENT : BRANCH_PALETTE[_branchIdx++ % BRANCH_PALETTE.length];
    });
  const branchColorByNodeId = {};
  nodesRaw.forEach((n) => {
    const idStr = String(n.node_id);
    branchColorByNodeId[idStr] = branchColorByRootId[topAncestorId(idStr)] || SYSTEM_ACCENT;
  });
  // Edges on the path from the selected node up to the root — highlighted to trace lineage.
  const selectedPathEdgeIds = new Set();
  if (selectedNodeId && selectedNodeId !== "root") {
    [selectedNodeId, ...ancestorChain(selectedNodeId, parentIdByChild)].forEach((id) => {
      if (id && id !== "root") selectedPathEdgeIds.add(`e-${id}`);
    });
  }

  const reactFlowNodes = [
    {
      id: "root",
      data: {
        label: customerLabel,
        parentId: -1,
        selected: selectedNodeId === "root",
        hasData: rootHasData,
        subtree: rootSubtree,
        direct: rootSubtree,
        costMuted: !costsActive,
        costExact: rootHasData ? formatValue(rootSubtree, "currency", false, 2) : undefined,
      },
      type: "selectorNode",
    },
    ...nodesRaw.map((n) => {
      const system = isSystemNode(n.code);
      const c = costByCode[n.code];
      const parentId = parentByChild[n.node_id];
      const parentCode = parentId != null ? codeByNodeId[parentId] : undefined;
      const parentSubtree = parentCode != null ? costByCode[parentCode]?.subtree : undefined;
      const hasData = c != null && ((c.subtree ?? 0) !== 0 || (c.direct ?? 0) !== 0);
      return {
        id: String(n.node_id),
        draggable: system ? false : undefined, // the catch-all can't be reparented
        data: {
          label: n.name,
          code: n.code,
          system,
          hasData,
          // In Draft the figure is the current Active cost shown as a read-only reference.
          costMuted: !costsActive,
          hasChildren: (childCountByParent[n.node_id] || 0) > 0,
          childCount: childCountByParent[n.node_id] || 0,
          collapsed: collapsedIds.has(String(n.node_id)),
          onToggleCollapse: handleToggleCollapse,
          change: draftDiff ? draftDiff.byCode[n.code] : null,
          direct: c?.direct ?? 0,
          subtree: c?.subtree ?? 0,
          pctOfParent: hasData ? pctOfParent(c?.subtree ?? 0, parentSubtree) : null,
          costExact: hasData ? formatValue(c?.subtree ?? 0, "currency", false, 2) : undefined,
          branchColor: branchColorByNodeId[String(n.node_id)],
          canEdit, // gates the hover drag handle + grab cursor (Active view is read-only)
          parentId: parentByChild[n.node_id] ?? -1,
          selected: String(n.node_id) === selectedNodeId,
        },
        type: "selectorNode",
      };
    }),
  ];
  const reactFlowEdges = edgesRaw.flatMap((e) => {
    const source = e.parent_node_id != null ? String(e.parent_node_id) : "root";
    const childId = String(e.child_node_id);
    // Calm neutral connectors so the cards lead. The only emphasis is the selected node's path to
    // the root: the same lines, drawn darker and heavier to trace lineage (no motion).
    const onPath = selectedPathEdgeIds.has(`e-${e.child_node_id}`);
    return [{
      id: `e-${e.child_node_id}`,
      source,
      target: childId,
      type: "step",
      style: onPath
        ? { stroke: "#475569", strokeWidth: 2.5 }
        : { stroke: "#cbd5e1", strokeWidth: 1.5 },
    }];
  });

  // Hide the subtrees of any collapsed node (and drop their connecting edges) before handing
  // the graph to the chart. The collapsed nodes themselves stay visible with a "+N" badge.
  const mappedEdges = edgesRaw.map((e) => ({
    parent: e.parent_node_id != null ? String(e.parent_node_id) : "root",
    child: String(e.child_node_id),
  }));
  // Collapsing a node REMOVES its subtree from the layout (rather than just hiding it), so dagre
  // re-packs and the freed space is reclaimed by the remaining nodes. The collapsed node itself
  // stays, with its "+N" badge. The chart keeps the toggled node steady on screen across the
  // re-layout (viewport compensation in OrgChart), so it doesn't jump.
  const collapseHidden = hiddenDescendants(collapsedIds, mappedEdges);
  let visibleNodes = reactFlowNodes.filter((n) => !collapseHidden.has(n.id));
  let visibleEdges = reactFlowEdges.filter((e) => !collapseHidden.has(e.source) && !collapseHidden.has(e.target));

  // Focus mode: show only the focused node and its subtree (it acts as a temporary root).
  // Guard against a stale focus on a node that no longer exists (falls back to the full tree).
  const focusValid = !!focusedNodeId && reactFlowNodes.some((n) => n.id === focusedNodeId);
  const focusedNodeName = focusValid
    ? (nodesRaw.find((n) => String(n.node_id) === focusedNodeId)?.name ?? "node")
    : null;
  if (focusValid) {
    const focusSet = new Set([focusedNodeId, ...hiddenDescendants(new Set([focusedNodeId]), mappedEdges)]);
    visibleNodes = visibleNodes.filter((n) => focusSet.has(n.id));
    visibleEdges = visibleEdges.filter((e) => focusSet.has(e.source) && focusSet.has(e.target));
  }

  // Nodes and edges load via separate queries, so an edge can momentarily reference a node that
  // hasn't loaded yet. Drop any edge whose endpoints aren't both present: otherwise dagre invents
  // phantom nodes for the missing ids (scattering the layout) and React Flow silently drops the
  // edge (no connector line), a corrupted state that can persist past the load.
  const visibleNodeIdSet = new Set(visibleNodes.map((n) => n.id));
  visibleEdges = visibleEdges.filter((e) => visibleNodeIdSet.has(e.source) && visibleNodeIdSet.has(e.target));

  const handleMoveNode = useCallback(
    (nodeId, newParentId, offerUndo = true) => {
      if (!selectedVersionId || !nodeId) return;
      const childId = nodeId === "root" ? null : Number.parseInt(nodeId, 10);
      if (Number.isNaN(childId)) return;
      const parentId = newParentId === "root" || !newParentId ? null : Number.parseInt(newParentId, 10);
      const movedName = nodeId === "root" ? customerLabel : (nodesRaw.find((n) => String(n.node_id) === String(nodeId))?.name ?? "Item");
      const parentName = !newParentId || newParentId === "root" ? customerLabel : (nodesRaw.find((n) => String(n.node_id) === String(newParentId))?.name ?? "parent");
      const currentEdges = edgesRaw.map((e) => ({
        parent_node_id: e.parent_node_id,
        child_node_id: e.child_node_id,
      }));
      // Original parent — captured before the move so Undo can put the node back.
      const originalParentId = currentEdges.find((e) => e.child_node_id === childId)?.parent_node_id ?? null;
      const withoutChild = currentEdges.filter((e) => e.child_node_id !== childId);
      const newEdges = [...withoutChild, { parent_node_id: Number.isNaN(parentId) ? null : parentId, child_node_id: childId }];
      return putCostModelEdges(selectedVersionId, { edges: newEdges })
        .then(() => {
          refetchTree();
          // Offer Undo only on a real reparent (not a drop onto the same parent), and not for
          // the undo move itself (offerUndo=false) so we don't chain undo-of-undo toasts.
          if (offerUndo && originalParentId !== parentId) {
            toast.success(`"${movedName}" moved under "${parentName}". This reassigns its allocated cost.`, {
              duration: 8000,
              action: {
                label: "Undo",
                onClick: () => handleMoveNode(nodeId, originalParentId == null ? "root" : String(originalParentId), false),
              },
            });
          } else {
            toast.success(`"${movedName}" moved under "${parentName}".`);
          }
        })
        .catch((err) => {
          toast.error(err?.message || "Could not move item. Please try again.");
        });
    },
    [selectedVersionId, edgesRaw, nodesRaw, customerLabel, refetchTree]
  );

  const handleDiscardDraft = useCallback(() => {
    if (!draftVersion) return;
    if (!window.confirm("Discard this draft? All unapplied changes will be lost.")) return;
    archiveCostModelVersion(draftVersion.id)
      .then(() => { setViewMode("active"); return awaitRefetchVersions(); })
      .then(() => { toast.success("Draft discarded."); })
      .catch((err) => toast.error(err?.message || "Could not discard draft. Please try again."));
  }, [draftVersion, awaitRefetchVersions]);

  const handleApplyDraft = useCallback(() => {
    if (!draftVersion) return;
    setApplyModalOpen(false);
    const appliedVersionId = draftVersion.id;
    applyCostModelVersion(appliedVersionId, { note: applyNote, effective_from: applyEffectiveDate, change_summary: changeSummaryText })
      .then(() => {
        setApplyNote("");
        return awaitRefetchVersions();
      })
      .then(() => {
        setViewMode("active");
        setJustAppliedVersionId(appliedVersionId);
        queryClient.invalidateQueries({ queryKey: ["cost-model-nodes", appliedVersionId] });
        queryClient.invalidateQueries({ queryKey: ["cost-model-edges", appliedVersionId] });
        toast.success("Draft applied. The hierarchy is now active.");
      })
      .catch((err) => {
        toast.error(err?.message || "Could not apply draft. Please try again.");
      });
  }, [draftVersion, applyNote, applyEffectiveDate, changeSummaryText, awaitRefetchVersions, queryClient]);

  const handleAddNode = useCallback(() => {
    if (!selectedVersionId) {
      toast.error("Select Active or Draft hierarchy to continue.");
      return;
    }
    const name = (addNodeName || "").trim();
    if (!name) {
      toast.error("Enter a name for the item.");
      return;
    }
    const parentId = addNodeParentId === "root" || !addNodeParentId ? null : addNodeParentId;
    const code = (addNodeCode || "").trim() || slugifyCode(name);
    setAddNodeCodeError(null);
    const parentNodeIdForLoading = parentId == null ? "root" : String(parentId);
    setAddingToParentId(parentNodeIdForLoading);
    createCostModelNode(selectedVersionId, {
      code,
      name,
      parent_node_id: parentId,
    })
      .then(() => {
        setAddNodeModalOpen(false);
        setAddNodeName("");
        setAddNodeCode("");
        setAddNodeCodeTouched(false);
        setAddNodeParentId(null);
        setAddNodeCodeError(null);
        refetchTree();
        toast.success("Item added to the hierarchy.");
      })
      .catch((err) => {
        const msg = err?.serverBody?.error?.message ?? err?.message ?? "Could not add item. Please try again.";
        setAddNodeCodeError(msg);
        setAddNodeAdvancedOpen(true);
        toast.error(msg);
      })
      .finally(() => {
        setAddingToParentId(null);
      });
  }, [selectedVersionId, addNodeName, addNodeCode, addNodeParentId, refetchTree]);

  const handleRenameNode = useCallback(() => {
    if (!selectedVersionId || !renameNode) return;
    const code = renameNode.code_editable ? ((renameCode || "").trim() || renameNode.code) : renameNode.code;
    setEditCodeError(null);
    updateCostModelNode(selectedVersionId, renameNode.node_id, {
      code,
      name: renameName.trim() || renameNode.name,
      status: renameNode.status || "active",
    })
      .then(() => {
        setRenameModalOpen(false);
        setRenameNode(null);
        setRenameName("");
        setRenameCode("");
        setEditCodeError(null);
        refetchTree();
        toast.success("Item updated.");
      })
      .catch((err) => {
        const msg = err?.serverBody?.error?.message ?? err?.message ?? "Could not update item. Please try again.";
        setEditCodeError(msg);
        setEditAdvancedOpen(true);
        toast.error(msg);
      });
  }, [selectedVersionId, renameNode, renameName, renameCode, refetchTree]);

  const handleMoveSubmit = useCallback(() => {
    if (!selectedVersionId || moveNodeId == null) return;
    const childId = Number.parseInt(moveNodeId, 10);
    const parentId = moveTargetParentId === "root" || moveTargetParentId === null ? null : moveTargetParentId;
    const movedName = nodesRaw.find((n) => String(n.node_id) === String(moveNodeId))?.name ?? "Item";
    const parentName = parentId == null ? customerLabel : (nodesRaw.find((n) => String(n.node_id) === String(parentId))?.name ?? "parent");
    const currentEdges = edgesRaw.map((e) => ({
      parent_node_id: e.parent_node_id,
      child_node_id: e.child_node_id,
    }));
    const withoutChild = currentEdges.filter((e) => e.child_node_id !== childId);
    const newEdges = [...withoutChild, { parent_node_id: parentId, child_node_id: childId }];
    putCostModelEdges(selectedVersionId, { edges: newEdges })
      .then(() => {
        setMoveModalOpen(false);
        setMoveNodeId(null);
        setMoveTargetParentId(null);
        refetchTree();
        toast.success(`"${movedName}" moved under "${parentName}".`);
      })
      .catch((err) => {
        toast.error(err?.message || "Could not move item. Please try again.");
      });
  }, [selectedVersionId, moveNodeId, moveTargetParentId, edgesRaw, nodesRaw, customerLabel, refetchTree]);

  const justApplied = activeVersionId === justAppliedVersionId;
  /** Status box describes the current view (Active or Draft). */
  const activeSubtitle = (() => {
    const recency = formatAppliedRecency(activeVersion?.applied_at, justApplied);
    const by = activeVersion?.applied_by_name ? ` by ${activeVersion.applied_by_name}` : "";
    return recency ? recency + by : "In use";
  })();
  // Toasts when view mode changes (clarity reinforcement); skip on initial mount
  const prevViewModeRef = useRef(viewMode);
  const isInitialMountRef = useRef(true);
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevViewModeRef.current = viewMode;
      return;
    }
    if (prevViewModeRef.current === viewMode) return;
    prevViewModeRef.current = viewMode;
    // if (viewMode === "draft") toast.info("Now viewing Draft. Changes are not active until applied.");
    // else toast.info("Now viewing Active. This is the hierarchy currently in effect.");
  }, [viewMode]);

  return (
    <Fragment>
      <AdministrationHeader
        title="Financial Structure"
        className="text-text-3"
        icon={icon({ name: "folder-tree", style: "light" })}
        showPeriodSelector={false}
      />
      <div className="flex flex-1 h-full">
        <div className="flex flex-1 flex-col relative min-h-0">
          {/* Draft change summary vs the active hierarchy — the blast radius before applying.
              Kept at page level (beside the draft actions), above the framed canvas. */}
          {viewMode === "draft" && draftDiff &&
            (draftDiff.counts.added + draftDiff.counts.moved + draftDiff.counts.renamed + draftDiff.counts.removed) > 0 && (
            <div className="mx-6 mt-4 px-4 py-2 rounded-card border border-card-bd bg-surface-2 text-text-2 text-sm flex items-center gap-3 flex-wrap">
              <span className="font-medium text-text">Draft vs active:</span>
              {draftDiff.counts.added > 0 && <span><span className="text-success font-medium">+{draftDiff.counts.added}</span> added</span>}
              {draftDiff.counts.moved > 0 && <span><span className="text-warning font-medium">{draftDiff.counts.moved}</span> moved</span>}
              {draftDiff.counts.renamed > 0 && <span><span className="text-primary font-medium">{draftDiff.counts.renamed}</span> renamed</span>}
              {draftDiff.counts.removed > 0 && (
                <span title={removedNames.join(", ")}>
                  <span className="text-text-2 font-medium">{draftDiff.counts.removed}</span> removed
                  {removedNames.length > 0 && <span className="text-text-2"> ({removedNames.join(", ")})</span>}
                </span>
              )}
              <button type="button" onClick={() => setReviewOpen(true)} className="ml-auto text-primary hover:underline font-medium">
                Review changes →
              </button>
            </div>
          )}
          {/* Framed canvas — a card holding the chart's own controls (perspective, view, find) and
              the org-chart canvas, giving the chart a clear workspace boundary. */}
          <div className="flex flex-1 flex-col min-h-0 mx-6 mt-4 mb-4 rounded-card border border-card-bd bg-card shadow-sm overflow-hidden">
            {/* Perspective tabs — a full-width underline strip across the top of the card. The
                active tab is marked by a primary underline (an inset bottom shadow that sits right
                on the strip line). */}
            <div className="flex items-center justify-between gap-4 px-4 border-b border-card-bd shrink-0">
              {dimensions.length > 0 ? (
                <div className="flex gap-1" role="tablist" aria-label="Perspective">
                  {dimensions.map((d) => {
                    const active = d.id === selectedDimensionId;
                    return (
                      <button
                        key={d.id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => setSelectedDimensionId(d.id)}
                        className={`relative inline-flex items-center gap-2 px-4 py-4 text-sm transition-colors ${
                          active
                            ? "text-primary font-semibold shadow-[inset_0_-3px_0_hsl(var(--primary))]"
                            : "text-text-2 font-medium hover:text-text"
                        }`}
                      >
                        <FontAwesomeIcon icon={getDimensionIcon(d)} className="text-sm" />
                        {d.name}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <span />
              )}
              {/* Version actions live on the perspective row — mirroring the budgets card header,
                  where the primary action sits in the table header. Period-lock context, plus
                  Create draft (Active view) or Discard / Apply draft (Draft view). */}
              <div className="flex shrink-0 items-center gap-2">
                {lockedThrough && (
                  <span
                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-ctrl border border-card-bd bg-surface-2 text-xs font-medium text-text-2 cursor-default"
                    data-tooltip-id="instantTooltip"
                    data-tooltip-content="Periods through this date are closed — allocations can't change. Manage closing in Application Parameters → Maintenance."
                  >
                    <FontAwesomeIcon icon={icon({ name: "lock", family: "sharp", style: "light" })} className="text-text-2" />
                    Locked through {formatLockDate(lockedThrough)}
                  </span>
                )}
                {viewMode === "active" && !hasDraft && (
                  <span data-tooltip-id="instantTooltip" data-tooltip-content="Creates a draft you can edit safely before applying.">
                    <button
                      type="button"
                      onClick={handleCreateDraft}
                      disabled={creatingDraft}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-ctrl bg-primary px-3 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={icon({ name: "plus" })} className="text-[13px]" />
                      Create draft
                    </button>
                  </span>
                )}
                {viewMode === "draft" && hasDraft && (
                  <>
                    <span data-tooltip-id="instantTooltip" data-tooltip-content="Discards the draft and its unapplied changes.">
                      <button
                        type="button"
                        onClick={handleDiscardDraft}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-ctrl border border-card-bd bg-card px-3 py-1.5 text-[13px] font-medium text-text transition-colors hover:bg-surface-2 disabled:opacity-50"
                      >
                        <FontAwesomeIcon icon={icon({ name: "trash-can" })} className="text-[13px]" />
                        Discard draft
                      </button>
                    </span>
                    <span data-tooltip-id="instantTooltip" data-tooltip-content="Applies the draft and replaces the active hierarchy.">
                      <button
                        type="button"
                        onClick={() => setApplyModalOpen(true)}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-ctrl bg-primary px-3 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-50"
                      >
                        <FontAwesomeIcon icon={icon({ name: "check" })} className="text-[13px]" />
                        Apply draft
                      </button>
                    </span>
                  </>
                )}
              </div>
            </div>
            {/* Below the line — find a node (left) and the Active / Draft view switch (right) */}
            <div className="flex flex-row items-center justify-between gap-4 px-4 py-3 shrink-0">
              {/* Find-a-node search: type to filter, select to center the chart on it
                  (revealing it if a collapsed ancestor was hiding it). */}
              <div className="flex items-center">
                {nodesRaw.length > 0 && (
                  <div className="relative">
                    <FontAwesomeIcon
                      icon={icon({ name: "magnifying-glass" })}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-text-3"
                    />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Escape") setSearchQuery(""); }}
                      placeholder="Find a node…"
                      aria-label="Find a node"
                      style={{ width: 260 }}
                      className="rounded-ctrl border border-card-bd bg-card py-1.5 pl-8 pr-3 text-[13px] text-text placeholder:text-text-3 focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    {searchMatches.length > 0 && (
                      <ul className="absolute left-0 z-30 mt-1 w-full max-h-64 overflow-auto bg-card border border-card-bd rounded-card shadow-lg">
                        {searchMatches.map((n) => (
                          <li key={n.node_id}>
                            <button
                              type="button"
                              onMouseDown={(e) => { e.preventDefault(); handleJumpToNode(n.node_id); }}
                              className="block w-full text-left px-3 py-2 text-sm text-text hover:bg-surface-2"
                            >
                              {n.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              {/* Viewing switch — segmented control matching the budgets status filter. Draft is
                  disabled until a draft exists; an indicator dot flags an available draft. */}
              <div
                aria-label="Viewing"
                className="inline-flex shrink-0 items-center rounded-seg border border-card-bd p-0.5"
              >
                {[
                  { value: "active", label: "Active" },
                  { value: "draft", label: "Draft" },
                ].map((opt) => {
                  const selected = viewMode === opt.value;
                  const disabled = opt.value === "draft" && !hasDraft;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      aria-pressed={selected}
                      disabled={disabled}
                      onClick={() => {
                        if (opt.value === "draft" && !hasDraft) return;
                        setViewMode(opt.value);
                      }}
                      className={`relative rounded-seg px-3 py-1 text-[13px] transition-colors ${
                        selected
                          ? "bg-primary font-semibold text-white"
                          : "text-text-2 hover:text-text"
                      } ${disabled ? "cursor-not-allowed opacity-40 hover:text-text-2" : ""}`}
                    >
                      {opt.label}
                      {opt.value === "draft" && hasDraft && !selected && (
                        <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Inner canvas — the dotted workspace sits inset, with white padding around it and its
                own rounded border, so it reads as a framed sheet inside the card. */}
            <div className="relative flex flex-1 min-h-0 px-4 pt-1 pb-4" style={{ overflowX: "clip" }}>
              <div
                ref={orgChartContainerRef}
                className="flex-1 min-w-0 h-full min-h-[420px] overflow-hidden relative rounded-card border border-card-bd"
                style={{ overscrollBehavior: "none", overscrollBehaviorX: "none" }}
              >
              {/* Focus banner: shown while a node's subtree is isolated; click to restore. */}
              {focusValid && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 text-xs bg-card border border-card-bd rounded-full px-3 py-1 shadow-sm">
                  <span className="text-text-2">Focused: <span className="font-medium text-text">{focusedNodeName}</span></span>
                  <button type="button" onClick={handleClearFocus} className="text-primary hover:underline font-medium">
                    Show full tree
                  </button>
                </div>
              )}
              {/* Period caption: figures reflect the trailing 12 months. In Draft they are the
                  current Active allocation shown as a read-only reference (the draft itself is
                  not yet applied). In Active view the toggle lets the user switch between
                  actuals and pro-forma (simulated, not-yet-booked) figures. */}
              {(anyCosts || proforma) && activeVersionId != null && (
                <div className="absolute top-2 left-2 z-10 flex items-center gap-2 text-xs bg-transparent px-1.5 py-0.5">
                  <span className="text-text-2">
                    {!costsActive
                      ? `Active cost · ${costPeriodLabel} (reference)`
                      : proforma
                      ? `Pro-forma · current rules · ${costPeriodLabel} · not booked`
                      : `Allocated cost · ${costPeriodLabel}`}
                  </span>
                  {activeSubtitle && (
                    <span className="text-text-2">· {activeSubtitle}</span>
                  )}
                  {costsActive && (
                    <button type="button" className="nodrag text-primary hover:underline pointer-events-auto" onClick={() => setProforma((p) => !p)}>
                      {proforma ? "Show actuals" : "Show pro-forma"}
                    </button>
                  )}
                </div>
              )}
              {/* Viewing chip and editing hint */}
              {/* {selectedVersionId != null && (
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-border bg-surface-2 px-2.5 py-0.5 text-xs font-medium text-text-2">
                    Viewing: {viewMode === "active" ? "Active" : "Draft"}
                  </span>
                  {viewMode === "active" && (
                    <span className="text-text-2 text-sm">
                      Editing is available in Draft.{" "}
                      <button
                        type="button"
                        onClick={switchToDraftOrCreate}
                        className="font-medium text-primary hover:underline"
                      >
                        {hasDraft ? "View draft" : "Create draft"}
                      </button>
                    </span>
                  )}
                </div>
              )} */}
              {viewMode === "active" && activeVersionId == null && selectedDimensionId != null && versionsQuery.isSuccess && (
                <div className="flex flex-col items-center justify-center mt-12 text-center pointer-events-none">
                  <FontAwesomeIcon
                    icon={icon({ name: "folder-tree", family: "sharp", style: "light" })}
                    className="text-text-2 text-3xl mb-4"
                  />
                  <p className="text-text-2 text-lg">No active hierarchy for this dimension</p>
                  <p className="text-text-2 text-sm">Create or apply a version to get started</p>
                </div>
              )}
              {dimensions.length === 0 && !dimensionsQuery.isFetching && dimensionsQuery.isSuccess && (
                <div className="flex flex-col items-center justify-center mt-4 text-center pointer-events-none">
                  <FontAwesomeIcon
                    icon={icon({ name: "layer-group", family: "sharp", style: "light" })}
                    className="text-text-2 text-3xl mb-4"
                  />
                  <p className="text-text-2 text-lg">No dimensions available</p>
                  <p className="text-text-2 text-sm">Add budget dimensions (e.g. org, product) in the administration schema (administration.budget_dimension)</p>
                </div>
              )}
              {reactFlowNodes.length > 0 && reactFlowEdges.length >= 0 && nodesQuery.isSuccess && edgesQuery.isSuccess && (
                <OrgChart
                  initialNodes={visibleNodes}
                  initialEdges={visibleEdges}
                  moveNode={canEdit ? handleMoveNode : () => Promise.resolve()}
                  externalLoadingNodeId={addingToParentId}
                  nodesDraggable={canEdit}
                  dimensionId={selectedDimensionId}
                  viewMode={viewMode}
                  anchorNodeId={lastToggledId}
                  focusReq={focusReq}
                  onFocusNode={handleFocusNode}
                  onSelectNode={setSelectedNodeId}
                />
              )}
            </div>

            {/* Inspector — node details + actions for the selected node. Docks on the right so
                the canvas keeps full height and nothing crowds the cards. */}
            {/* Inspector — always mounted so it slides in/out like the shared RightSheet drawer:
                the outer wrapper animates width + opacity, the inner panel slides on transform. */}
            {/* Inspector overlays the right of the canvas (absolute, out of flex flow) so it never
                shrinks/clips the chart; it slides in/out from the right edge like the shared
                RightSheet. The row's `overflow-x: clip` hides it off-screen when closed. */}
            <aside
              aria-hidden={!showActionBar}
              className={`absolute right-4 top-1 bottom-4 z-20 w-[320px] transition-transform duration-200 ease-out ${showActionBar ? "translate-x-0 pointer-events-auto" : "translate-x-[120%] pointer-events-none"}`}
            >
              <div className="w-[320px] h-full border border-card-bd rounded-card bg-card shadow-lg flex flex-col overflow-auto">
                {/* Header — mirrors the shared RightSheet drawer header for consistency. */}
                <div className="flex items-start justify-between px-4 py-3 border-b border-card-bd flex-shrink-0">
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold text-text truncate">{selName}</h2>
                    {selNode?.code && <p className="text-xs text-text-2 mt-0.5">{selNode.code}</p>}
                    {selSystem && <p className="text-xs text-text-2 mt-0.5">System node · read-only</p>}
                    {selIsRoot && <p className="text-xs text-text-2 mt-0.5">Organisation root</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedNodeId(null)}
                    aria-label="Close inspector"
                    className="ml-4 p-1.5 rounded hover:bg-surface-2 transition-colors cursor-pointer flex-shrink-0"
                  >
                    <FontAwesomeIcon icon={icon({ name: "xmark", style: "solid" })} className="text-text-2 text-sm" />
                  </button>
                </div>

                <div className="px-4 py-3 border-b border-card-bd flex-shrink-0">
                  <div className="text-xs text-text-2">{costsActive ? `Allocated · ${costPeriodLabel}` : `Active cost · ${costPeriodLabel}`}</div>
                  <div className="text-lg font-semibold text-text">
                    {(() => {
                      if (selIsRoot) return rootHasData ? formatValue(rootSubtree, "currency", false, 2) : "—";
                      const c = costByCode[selNode?.code];
                      return c && ((c.subtree ?? 0) !== 0 || (c.direct ?? 0) !== 0) ? formatValue(c.subtree, "currency", false, 2) : "—";
                    })()}
                  </div>
                </div>

                <div className="p-4 flex flex-col gap-2">
                  {canEdit && !selSystem && (
                    <InspectorAction
                      variant="primary"
                      onClick={() => onAddChild(selIsRoot ? "root" : selectedNodeId)}
                      iconDef={icon({ name: "plus", family: "sharp", style: "light" })}
                      label="Add child"
                      kbd="A"
                    />
                  )}
                  {canEdit && !selSystem && !selIsRoot && (
                    <InspectorAction
                      onClick={openRenameSelected}
                      iconDef={icon({ name: "pen", family: "sharp", style: "light" })}
                      label="Rename"
                      kbd="R"
                    />
                  )}
                  {canEdit && !selSystem && !selIsRoot && (
                    <InspectorAction
                      onClick={openMoveSelected}
                      iconDef={icon({ name: "arrows-up-down-left-right", family: "sharp", style: "light" })}
                      label="Move to…"
                      kbd="M"
                    />
                  )}
                  {!selIsRoot && !selSystem && (
                    <InspectorAction
                      onClick={() => setHistoryNode({ id: selectedNodeId, name: selName })}
                      iconDef={icon({ name: "clock-rotate-left", family: "sharp", style: "light" })}
                      label="History"
                      kbd="H"
                    />
                  )}
                  {canEdit && !selSystem && !selIsRoot && (
                    <>
                      <div className="my-1 border-t border-card-bd" />
                      <InspectorAction
                        variant="danger"
                        onClick={() => onRemoveNode(selectedNodeId, selName)}
                        iconDef={icon({ name: "trash-can", family: "sharp", style: "light" })}
                        label="Remove"
                        kbd="Del"
                      />
                    </>
                  )}
                  {selSystem && <p className="text-xs text-text-2">This bucket absorbs unallocated cost. It is managed by the platform and can't be edited.</p>}
                  {!canEdit && !selSystem && <p className="text-xs text-text-2">Switch to the Draft view to edit the structure.</p>}
                </div>
              </div>
            </aside>
            </div>
          </div>
        </div>
      </div>


      <NodeHistoryDrawer
        open={!!historyNode}
        onClose={() => setHistoryNode(null)}
        node={historyNode}
        versionId={selectedVersionId}
      />

      {/* Review-changes panel — shared RightSheet drawer; every draft change with before→after,
          click a row to jump to it. */}
      <RightSheet
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        title="Review changes"
        subtitle={`Draft vs active — applies effective ${applyEffectiveDate}`}
      >
        {reviewChanges.length === 0 && <p className="text-sm text-text-2">No changes vs the active hierarchy.</p>}
        <ul className="space-y-2">
          {reviewChanges.map((c) => (
            <li key={`${c.kind}-${c.code}`}>
              <button
                type="button"
                onClick={() => reviewJumpTo(c.nodeId)}
                disabled={!c.nodeId}
                className={`w-full text-left rounded-ctrl border border-card-bd px-3 py-2 ${c.nodeId ? "hover:bg-surface-2 cursor-pointer" : "cursor-default opacity-80"}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center h-5 px-2 rounded-full text-[10px] font-medium border ${
                    c.kind === "added" ? "bg-success-soft text-success border-success-soft"
                    : c.kind === "moved" ? "bg-warning-soft text-warning border-warning-soft"
                    : c.kind === "renamed" ? "bg-primary-soft text-primary border-primary-soft"
                    : "bg-surface-2 text-text-2 border-card-bd"
                  }`}>{c.kind}</span>
                  <span className="text-sm font-medium text-text truncate">{c.name}</span>
                </div>
                <div className="text-xs text-text-2 mt-1">{c.detail}</div>
              </button>
            </li>
          ))}
        </ul>
      </RightSheet>

      {/* Apply Draft Modal */}
      <Dialog
        show={applyModalOpen}
        close={() => setApplyModalOpen(false)}
        onEnter={handleApplyDraft}
        title="Apply Draft"
        subtitle="Hierarchy"
        footer={
          <>
            <FormButton
              title="Apply"
              style="primary"
              onClick={handleApplyDraft}
            />
            <FormButton
              title="Cancel"
              style="secondary"
              onClick={() => setApplyModalOpen(false)}
            />
          </>
        }
      >
        <p className="text-text-2 mb-4">This will apply the draft and make it the active hierarchy. Continue?</p>
        {changeSummaryText && (
          <p className="text-sm text-text-2 mb-4">Changes: <span className="font-medium text-text">{changeSummaryText}</span></p>
        )}
        <label htmlFor="apply-effective" className="block text-sm text-text-2 mb-1">Effective from</label>
        <input
          id="apply-effective"
          type="date"
          value={applyEffectiveDate}
          onChange={(e) => setApplyEffectiveDate(e.target.value)}
          min={lockedThrough ? new Date(new Date(lockedThrough).getTime() + 86400000).toISOString().slice(0,10) : undefined}
          className="border-card-bd border rounded-ctrl p-2 w-[400px] mb-4"
        />
        {lockedThrough && applyEffectiveDate <= lockedThrough && (
          <p className="text-xs text-warning mt-1">Locked through {lockedThrough} — choose a later date.</p>
        )}
        <p className="text-xs text-text-2 mb-4 -mt-3">The date the new structure takes effect. Earlier allocations stay on the prior structure.</p>
        <label htmlFor="apply-note" className="block text-sm text-text-2 mb-1">Optional note (for audit)</label>
        <input
          id="apply-note"
          type="text"
          value={applyNote}
          onChange={(e) => setApplyNote(e.target.value)}
          className="border-card-bd border rounded-ctrl p-2 w-[400px]"
          placeholder="Note"
        />
      </Dialog>

      {/* Add Node Modal */}
      <Dialog
        show={addNodeModalOpen}
        close={() => !addingToParentId && setAddNodeModalOpen(false)}
        onEnter={() => { if (!addingToParentId) handleAddNode(); }}
        title="Add node"
        subtitle="Hierarchy"
        footer={
          <>
            <FormButton
              title={addingToParentId ? "Adding…" : "Add"}
              style="primary"
              disabled={!selectedVersionId || !!addingToParentId}
              onClick={handleAddNode}
            />
            <FormButton
              title="Cancel"
              style="secondary"
              disabled={!!addingToParentId}
              onClick={() => !addingToParentId && setAddNodeModalOpen(false)}
            />
          </>
        }
      >
        {!selectedVersionId && (
          <p className="text-warning text-sm mb-2">Select Active or Draft hierarchy first, or create a draft.</p>
        )}
        <div className="space-y-3">
          <div>
            <label htmlFor="add-node-name" className="block text-sm text-text-2">Name *</label>
            <input
              id="add-node-name"
              autoFocus
              type="text"
              value={addNodeName}
              onChange={(e) => setAddNodeName(e.target.value)}
              placeholder="Required"
              className="border-card-bd border rounded-ctrl p-2 w-[400px]"
            />
            {addDuplicateWarning && <p className="text-xs text-warning mt-1">{addDuplicateWarning}</p>}
          </div>
          <div>
            <button
              type="button"
              onClick={() => setAddNodeAdvancedOpen((o) => !o)}
              className="flex items-center gap-2 text-sm text-text-2 hover:text-text"
              aria-expanded={addNodeAdvancedOpen}
            >
              {addNodeAdvancedOpen ? (
                <FontAwesomeIcon icon={icon({ name: "chevron-down", style: "solid" })} className="w-4 h-4" />
              ) : (
                <FontAwesomeIcon icon={icon({ name: "chevron-right", style: "solid" })} className="w-4 h-4" />
              )}
              Advanced
            </button>
            {addNodeAdvancedOpen && (
              <div className="mt-2 pl-4 border-l-2 border-card-bd space-y-1">
                <label htmlFor="add-node-code" className="block text-sm text-text-2">Code</label>
                <input
                  id="add-node-code"
                  type="text"
                  value={addNodeCode}
                  onChange={(e) => {
                    setAddNodeCode(e.target.value);
                    setAddNodeCodeTouched(true);
                  }}
                  onBlur={() => setAddNodeCode((c) => slugifyCode(c) || c || "node")}
                  className={`border rounded-ctrl p-2 w-[400px] ${addNodeCodeError ? "border-warning" : "border-card-bd"}`}
                />
                <p className="text-text-2 text-xs">Used for stable references, integrations, and audit history.</p>
                <p className="text-text-2 text-xs italic">
                  {addNodeCodeTouched ? "Custom code." : "Auto-generated from name."}
                </p>
                {addNodeCodeError && (
                  <p className="text-warning text-sm mt-1" role="alert">
                    {addNodeCodeError}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </Dialog>

      {/* Edit Node Modal */}
      <Dialog
        show={renameModalOpen}
        close={() => setRenameModalOpen(false)}
        onEnter={handleRenameNode}
        title="Edit node"
        subtitle="Hierarchy"
        footer={
          <>
            <FormButton
              title="Save"
              style="primary"
              onClick={handleRenameNode}
            />
            <FormButton
              title="Cancel"
              style="secondary"
              onClick={() => setRenameModalOpen(false)}
            />
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label htmlFor="rename-node-name" className="block text-sm text-text-2">Name</label>
            <input
              id="rename-node-name"
              type="text"
              value={renameName}
              autoFocus
              onChange={(e) => setRenameName(e.target.value)}
              className="border-card-bd border rounded-ctrl p-2 w-[400px]"
            />
            {renameDuplicateWarning && <p className="text-xs text-warning mt-1">{renameDuplicateWarning}</p>}
          </div>
          <div>
            <button
              type="button"
              onClick={() => setEditAdvancedOpen((o) => !o)}
              className="flex items-center gap-2 text-sm text-text-2 hover:text-text"
              aria-expanded={editAdvancedOpen}
            >
              {editAdvancedOpen ? (
                <FontAwesomeIcon icon={icon({ name: "chevron-down", style: "solid" })} className="w-4 h-4" />
              ) : (
                <FontAwesomeIcon icon={icon({ name: "chevron-right", style: "solid" })} className="w-4 h-4" />
              )}
              Advanced
            </button>
            {editAdvancedOpen && renameNode && (
              <div className="mt-2 pl-4 border-l-2 border-card-bd space-y-1">
                <div className="flex items-center gap-2">
                  <label htmlFor="edit-node-code" className="block text-sm text-text-2">Code</label>
                  {renameNode.code_editable && (
                    <button
                      type="button"
                      onClick={() => {
                        setRenameCode(slugifyCode(renameName));
                        setRenameCodeTouched(false);
                      }}
                      className="text-xs text-text-2 hover:text-text underline"
                    >
                      Regenerate
                    </button>
                  )}
                </div>
                <input
                  id="edit-node-code"
                  type="text"
                  value={renameCode}
                  onChange={(e) => {
                    setRenameCode(e.target.value);
                    setRenameCodeTouched(true);
                  }}
                  onBlur={() => renameNode.code_editable && setRenameCode((c) => slugifyCode(c) || c || "node")}
                  readOnly={!renameNode.code_editable}
                  disabled={!renameNode.code_editable}
                  className={`border rounded-ctrl p-2 w-[400px] ${!renameNode.code_editable ? "bg-surface-2 text-text-2" : ""} ${editCodeError ? "border-warning" : "border-card-bd"}`}
                />
                {renameNode.code_editable ? (
                  <p className="text-text-2 text-xs">Used for stable references, integrations, and audit history.</p>
                ) : (
                  <p className="text-text-2 text-xs">Locked because this node is used in budgets or an applied hierarchy.</p>
                )}
                {renameNode.code_editable && (
                  <p className="text-text-2 text-xs italic">
                    {renameCodeTouched ? "Custom code." : "Auto-generated from name."}
                  </p>
                )}
                {editCodeError && (
                  <p className="text-warning text-sm mt-1" role="alert">
                    {editCodeError}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </Dialog>

      {/* Move Node Modal */}
      <Dialog
        show={moveModalOpen}
        close={() => setMoveModalOpen(false)}
        onEnter={handleMoveSubmit}
        title="Move Node"
        subtitle="Hierarchy"
        footer={
          <>
            <FormButton
              title="Move"
              style="primary"
              onClick={handleMoveSubmit}
            />
            <FormButton
              title="Cancel"
              style="secondary"
              onClick={() => setMoveModalOpen(false)}
            />
          </>
        }
      >
        <label htmlFor="move-node-parent" className="block text-sm text-text-2 mb-1">New parent</label>
        <select
          id="move-node-parent"
          value={moveTargetParentId === null ? "root" : moveTargetParentId}
          onChange={(e) => setMoveTargetParentId(e.target.value === "root" ? null : e.target.value)}
          className="mb-4 w-full rounded border border-card-bd bg-card text-text px-2 py-1"
        >
          <option value="root">Root</option>
          {nodesRaw
            .filter((n) => String(n.node_id) !== String(moveNodeId))
            .map((n) => (
              <option key={n.node_id} value={n.node_id}>
                {n.name}
              </option>
            ))}
        </select>
      </Dialog>

      {/* Remove Node Modal */}
      <Dialog
        show={removeModalOpen}
        close={() => !removingNode && setRemoveModalOpen(false)}
        onEnter={() => { if (!removingNode) handleRemoveNode(); }}
        title="Remove Node"
        subtitle="Hierarchy"
        footer={
          <>
            <FormButton
              title={removingNode ? "Removing…" : "Remove"}
              style="danger"
              disabled={removingNode}
              onClick={handleRemoveNode}
            />
            <FormButton
              title="Cancel"
              style="secondary"
              disabled={removingNode}
              onClick={() => !removingNode && setRemoveModalOpen(false)}
            />
          </>
        }
      >
        <p className="text-text-2 mb-4">
          Remove &quot;{removeNodeName}&quot; from the model? Any child nodes will become root-level nodes.
        </p>
      </Dialog>
      <Tooltip />
    </Fragment>
  );
};

export default HierarchyManager;
